import './MainMenu.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Sidebar, Menu, Divider, Icon, Popup } from 'semantic-ui-react';
import { useAuth } from '../context/AuthContext';
import { analyticsTrack } from '../helpers/AnalyticsUtility';
import { canIUse, AppFeatures, Features } from '../helpers/PermissionUtility';
import { useAppSelector } from '../hooks/ReduxHooks';
import { CloseDarkSmall, GoFormzLogoBlue, GoFormzLogoIconSmall, GoFormzLogoIcon, SpecialistIcon } from '../icons/icons';
import MainMenuUserMenu from './MainMenuUserMenu';
import { useUserPersistedState } from '../persistedstate/users/useUserPersistedState';
import { getQuerystringParameters } from '../helpers/utility';
import { SubscriptionCodes } from '../helpers/constants';
import NotificationCenter from '../notifications/components/NotificationCenter';
import classNames from 'classnames';
import { useClientPersistedState } from '../persistedstate/clients/useClientPersistedState';

export type MainMenuProps = {
  children?: React.ReactNode;
  onSuccessLauncherClick: () => void;
};

export default function MainMenu(props: MainMenuProps) {
  const companyName = useAppSelector<string>((state) => state.profile.settings.displayName || state.profile.client.name);
  const isAdmin = useAppSelector<boolean>((state) => state.profile.isAdmin);
  const firstName = useAppSelector<string>((state) => state.profile.user.firstName);
  const lastName = useAppSelector<string>((state) => state.profile.user.lastName);
  const emailAddress = useAppSelector<string>((state) => state.profile.user.email);
  const userId = useAppSelector<string>((state) => state.profile.user.id);
  const hasProfilePicture = useAppSelector((state) => state.profile.settings?.hasProfilePicture || state.profile.user?.hasProfilePicture);
  const isInTrial = useAppSelector<boolean>((state) => state.profile.isInTrial);
  const trialDaysLeft = useAppSelector<number>((state) => state.profile.trialDaysLeft);
  const dunningStatus = useAppSelector<boolean>((state) => state.profile.client.dunningStatus);
  const subscription = useAppSelector<string>((state) => state.profile.client?.subscription);
  const companySize = useAppSelector<string>((state) => state.profile.client?.companySize);
  const upgradeRequired = useMemo(() => subscription === SubscriptionCodes.Basic && !isInTrial, [subscription, isInTrial]);
  const { isIndependentUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const [firstTemplateStarted] = useUserPersistedState('firstTemplateStarted', true);
  const [firstTemplatePublished] = useUserPersistedState('firstTemplatePublished', true);
  const [dismissedOnboarding] = useUserPersistedState('dismissGetStartedOnboarding');
  const [signupDomain] = useClientPersistedState('signupDomain');
  const [hideMainMenu, setHideMainMenu] = useState(false);
  const [disableMenuItems, setDisableMenuItems] = useState(false);
  const [showSpeakWithASpecialist, setShowSpeakWithASpecialist] = useState(false);

  useEffect(() => {
    if ((isInTrial || subscription === 'gf-basic') && isAdmin) {
      setShowSpeakWithASpecialist(true);
    } else {
      setShowSpeakWithASpecialist(false);
    }
  }, [isInTrial, subscription, companySize, signupDomain, isAdmin]);

  const params = getQuerystringParameters(true);
  const previewMode = params.preview === 'true';

  useEffect(() => {
    // ensure feature flag is enabled
    if (canIUse(Features.DisableOnboardingNavigation)) {
      // hide the main menu if the user is in the onboarding flow
      setHideMainMenu(!firstTemplateStarted && isInTrial && !dismissedOnboarding);
      setDisableMenuItems(!firstTemplatePublished && isInTrial && !dismissedOnboarding);
    }

    // hide the main menu if the user is in form preview mode
    if (previewMode) {
      setHideMainMenu(true);
    }
  }, [firstTemplateStarted, firstTemplatePublished, dismissedOnboarding, previewMode, isInTrial]);

  useEffect(() => {
    window.addEventListener('resize', closeSidebar, { capture: true });

    return () => {
      window.removeEventListener('resize', closeSidebar, { capture: true });
    };
  }, []);

  useEffect(() => {
    closeSidebar();
  }, [location]);

  const menuItems = [
    {
      text: 'Welcome',
      route: '/home',
      visible: canIUse(AppFeatures.Dashboard)
    },
    {
      text: 'Templates',
      route: '/templates',
      visible: canIUse(AppFeatures.TemplateList)
    },
    {
      text: 'Forms',
      route: '/forms',
      visible: true,
      disabledForOnboarding: true,
      disabledMessage: 'Publish your template to begin filling out forms.'
    },
    {
      text: 'Public Forms',
      route: '/publicforms',
      disabledForOnboarding: true,
      visible: canIUse(AppFeatures.PublicShare)
    },
    {
      text: 'Reports',
      route: '/reports',
      disabledForOnboarding: true,
      visible: canIUse(AppFeatures.Reports) || isAdmin
    },
    {
      text: 'Workflows',
      route: '/workflows/dashboard',
      disabledForOnboarding: true,
      visible: canIUse(AppFeatures.Workflow) || isAdmin
    },
    {
      text: 'DataSources',
      route: '/datasource',
      disabledForOnboarding: true,
      visible: canIUse(AppFeatures.ManageDataSources)
    }
  ]
    .filter((item) => item.visible)
    .map((item) => (
      <React.Fragment key={`nav-menu-${item.text}`}>
        {item.disabledForOnboarding && disableMenuItems ? (
          <Popup
            position="bottom left"
            trigger={<span className="menu-link disabled">{item.text}</span>}
            content={<span>{item.disabledMessage ?? 'Publish your template to unlock.'}</span>}
          />
        ) : (
          <NavLink
            className={({ isActive }) =>
              classNames('menu-link', { active: isActive || (item.text === 'Workflows' && location.pathname.includes('/workflows')) })
            }
            to={item.route}
          >
            {item.text}
          </NavLink>
        )}
      </React.Fragment>
    ));

  const openSidebar = () => {
    setSidebarOpen(true);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const onHelpClick = () => {
    if (canIUse(Features.DisableSuccessLauncher)) {
      analyticsTrack('Help Question Mark - Selected');
      return window.open('http://help.goformz.com', '_blank');
    }

    return props.onSuccessLauncherClick();
  };

  const mobileMenu = (
    <Sidebar as={Menu} animation="overlay" icon="labeled" inverted onHide={closeSidebar} vertical visible={sidebarOpen} className="side-bar mobile-main-menu">
      <div onClick={closeSidebar} className="close-icon">
        <CloseDarkSmall />
      </div>
      <div className="logo-wrapper">
        <GoFormzLogoBlue />
      </div>
      <Divider fitted={true} />
      <nav className="mobile-menu-items">{menuItems}</nav>
    </Sidebar>
  );

  const desktopMenu = (
    <nav className="main-menu">
      <div className="mobile-left-menu">
        <Icon name="bars" onClick={openSidebar} />
        <Link className="gf-mobile-logo-wrapper" to={'/home'}>
          <GoFormzLogoIconSmall />
        </Link>
      </div>
      <Link to={'/home'} className="gf-logo">
        <GoFormzLogoIcon />
      </Link>

      <nav className="desktop-menu disable-selection">{menuItems}</nav>

      <div className="right-menu disable-selection">
        {showSpeakWithASpecialist && (
          <React.Fragment>
            <a
              className="specialist"
              href="https://www.goformz.com/talk-to-a-human"
              target="_blank"
              onClick={() => analyticsTrack('Speak with a Specialist Clicked', {})}
            >
              <span className="specialist-text">Speak with a Specialist</span>
              <div className="specialist-icon">
                <SpecialistIcon />
              </div>
            </a>
          </React.Fragment>
        )}
        {(isInTrial || upgradeRequired) && !dunningStatus && !canIUse(Features.PublicFormsPilot) && canIUse(AppFeatures.ManageSubscription) && (
          <React.Fragment>
            <Link
              className={`trial row${trialDaysLeft < 4 ? ' red' : ''}`}
              to={canIUse(AppFeatures.ManageSubscription) ? '/subscription' : '/'}
              onClick={() => {
                trialDaysLeft != 0 ? analyticsTrack('Trial Countdown Clicked', {}) : analyticsTrack('Expired Trial Countdown Clicked', {});
              }}
            >
              <div className="trial-days">
                <div className={`trial-days-left${trialDaysLeft > 999 ? ' small' : trialDaysLeft > 99 ? ' medium' : ' large'}`}>{trialDaysLeft}</div>
              </div>
              <div className="trial-days-text">
                <div className="days-left">{trialDaysLeft != 1 ? 'Days left in your Trial' : 'Day left in your Trial'}</div>
                <div className="ready">Ready to Subscribe?</div>
              </div>
            </Link>
            <div className="vertical-line" />
          </React.Fragment>
        )}
        <div className="help-support" title="Help &amp; Support" onClick={onHelpClick}>
          <Icon name="question circle" />
        </div>
        <NotificationCenter />
        <MainMenuUserMenu
          companyName={companyName}
          emailAddress={emailAddress}
          firstName={firstName}
          lastName={lastName}
          userId={userId}
          hasProfilePicture={hasProfilePicture}
          isIndependentUser={isIndependentUser}
        />
      </div>
    </nav>
  );
  return (
    !hideMainMenu && (
      <>
        {mobileMenu}
        {desktopMenu}
      </>
    )
  );
}
